import { createAsyncThunk } from "@reduxjs/toolkit";
import API, { showNetworkErrorToast } from "Utils/api";
import {
  setMasters,
  setVersions,
  setVersionsLoading,
  addVersion,
  setLoading,
  setError,
  updateMaster,
} from ".";

// Fetch all master missions
export const fetchMissions = createAsyncThunk(
  "missions/fetchAll",
  async (_, thunkAPI) => {
    try {
      thunkAPI.dispatch(setLoading(true));
      const response = await API.get("/missions/");
      const missions = response.data.results || response.data;
      thunkAPI.dispatch(setMasters(missions));
      thunkAPI.dispatch(setLoading(false));
      return missions;
    } catch (error) {
      thunkAPI.dispatch(setLoading(false));
      thunkAPI.dispatch(setError("Failed to fetch missions"));
      showNetworkErrorToast(error);
      return Promise.reject();
    }
  }
);

// Fetch versions for a specific master mission
export const fetchVersions = createAsyncThunk(
  "missions/fetchVersions",
  async (masterId: string, thunkAPI) => {
    try {
      thunkAPI.dispatch(setVersionsLoading({ masterId, loading: true }));
      const response = await API.get(`/missions/${masterId}/versions/`);
      const versions = response.data.results || response.data;
      thunkAPI.dispatch(setVersions({ masterId, versions }));
      return { masterId, versions };
    } catch (error) {
      thunkAPI.dispatch(setVersionsLoading({ masterId, loading: false }));
      thunkAPI.dispatch(setError(`Failed to fetch versions for ${masterId}`));
      showNetworkErrorToast(error);
      return Promise.reject();
    }
  }
);

// Fetch latest version for a master mission
export const fetchLatestVersion = createAsyncThunk(
  "missions/fetchLatestVersion",
  async (masterId: string, thunkAPI) => {
    try {
      thunkAPI.dispatch(setVersionsLoading({ masterId, loading: true }));
      const response = await API.get(`/missions/${masterId}/versions/latest/`);
      const version = response.data;

      // Add to versions cache
      thunkAPI.dispatch(addVersion({ masterId, version }));
      thunkAPI.dispatch(setVersionsLoading({ masterId, loading: false }));

      return { masterId, version };
    } catch (error) {
      thunkAPI.dispatch(setVersionsLoading({ masterId, loading: false }));
      thunkAPI.dispatch(
        setError(`Failed to fetch latest version for ${masterId}`)
      );
      showNetworkErrorToast(error);
      return Promise.reject();
    }
  }
);

// Create new version (edit operation)
export const createVersion = createAsyncThunk(
  "missions/createVersion",
  async (
    {
      masterId,
      versionData,
    }: { masterId: string; versionData: Partial<Version> },
    thunkAPI
  ) => {
    try {
      // Optimistic update - add temporary version
      const tempVersion: Version = {
        id: `temp-${Date.now()}`,
        master_mission: masterId,
        version_number: 0, // Will be set by backend
        name: versionData.name || "",
        short_name: versionData.short_name || null,
        default: false,
        previous: versionData.previous || "",
        loops: versionData.loops || null,
        is_uber: versionData.is_uber || false,
        time_stamp: null,
        updated_at: new Date().toISOString(),
        semantic_map: versionData.semantic_map || "",
        stops: versionData.stops || [],
      };

      thunkAPI.dispatch(addVersion({ masterId, version: tempVersion }));

      // Make API call
      const response = await API.post(
        `/missions/${masterId}/versions/`,
        versionData
      );
      const newVersion = response.data;

      // Update master mission's latest_version
      const masterResponse = await API.get(`/missions/${masterId}/`);
      thunkAPI.dispatch(updateMaster(masterResponse.data));

      // Replace temp version with real version
      // This is handled by refetching versions
      thunkAPI.dispatch(fetchVersions(masterId));

      // Don't show toast here - let the calling component handle it
      return { masterId, version: newVersion };
    } catch (error) {
      // Remove optimistic update on failure
      thunkAPI.dispatch(fetchVersions(masterId));
      thunkAPI.dispatch(setError("Failed to create version"));
      showNetworkErrorToast(error);
      return Promise.reject();
    }
  }
);

// Fetch a specific master mission by ID
export const fetchMissionById = createAsyncThunk(
  "missions/fetchById",
  async (masterId: string, thunkAPI) => {
    try {
      thunkAPI.dispatch(setLoading(true));
      const response = await API.get(`/missions/${masterId}/`);
      const master = response.data;

      // Update or add the master
      thunkAPI.dispatch(updateMaster(master));
      thunkAPI.dispatch(setLoading(false));

      return master;
    } catch (error) {
      thunkAPI.dispatch(setLoading(false));
      thunkAPI.dispatch(setError(`Failed to fetch mission ${masterId}`));
      showNetworkErrorToast(error);
      return Promise.reject();
    }
  }
);
